import { toast } from "react-toastify";
import { Recipient } from "../../../shared/models/user/recipient/recipient";
import { User } from "../../../shared/models/user/user";
import allUsersService, {
  UserUpdateMeta,
} from "../../../../services/all-users.service";
import { AxiosError } from "axios";
import { SaveResultType } from "../../../shared/enum/save-result-type";
import recipientService from "../../../../services/recipient.service";
import { FileModel } from "../../../shared/models/FileModel";
import Joi from "joi";
import { recipientErrors } from "../../../../config/constants";

export function initSuccessToast(message: string) {
  toast["success"](message, {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
}

export function handleUserSave(
  currentUser: User,
  unmodifiedUser: User,
): Promise<{
  status: SaveResultType;
  payload: any;
}> {
  const relatedRecipient = currentUser.relatedRecipientId || ({} as Recipient);
  const unmodifiedRecipient =
    unmodifiedUser.relatedRecipientId || ({} as Recipient);

  const userRequestBody: User = {} as User;
  const recipientRequestBody: Recipient = {} as Recipient;

  Object.keys(currentUser).forEach((key) => {
    if (
      (currentUser as any)[key] !== (unmodifiedUser as any)[key] &&
      key !== "relatedRecipientId"
    ) {
      (userRequestBody as any)[key] = (currentUser as any)[key] || null;
    }
  });

  Object.keys(relatedRecipient).forEach(key => {
    if ((relatedRecipient as any)[key] !== (unmodifiedRecipient as any)[key]) {
      (recipientRequestBody as any)[key] = (relatedRecipient as any)[key] || null;
    }
  })

  const userWasModified = !!Object.keys(userRequestBody).length;
  const recipientWasModified = !!Object.keys(recipientRequestBody).length;

  if (currentUser.userId) {
    if (userWasModified && recipientWasModified) {
      return saveUser(currentUser.userId, userRequestBody).then(
        (userSaveResponse) => {
          if (userSaveResponse.status === 0) {
            return saveRecipient(
              recipientRequestBody,
              unmodifiedRecipient,
              false
            );
          } else {
            return userSaveResponse;
          }
        },
      );
    } else if (userWasModified && !recipientWasModified) {
      return saveUser(currentUser.userId, userRequestBody);
    } else if (!userWasModified && recipientWasModified) {
      return saveRecipient(relatedRecipient, unmodifiedRecipient, false);
    }
  }
  return Promise.resolve({ status: SaveResultType.NO_CHANGE, payload: {} });
}

export function saveUser(currentUserId: number, userRequestBody: User) {
  return allUsersService
    .editUser(currentUserId, userRequestBody)
    .then((response: UserUpdateMeta) => {
      if (response.acknowledged) {
        return { status: SaveResultType.SUCCESS, payload: {} };
      }
    })
    .catch((e) => {
      if (e instanceof AxiosError) {
        return e.response?.data;
      }
    });
}

export function saveRecipient(
  updatedRecipient: Recipient,
  recipient: Recipient,
  isNewRecipient: boolean,
): Promise<{
  status: SaveResultType;
  payload: any;
}> {
  const formData = new FormData();

  if (!isNewRecipient) {
    formData.append("recipientId", "" + recipient.recipientId);
  }

  if (!Object.keys(updatedRecipient).length) {
    return Promise.resolve({ status: SaveResultType.ERROR, payload: recipientErrors });
  }

  Object.keys(updatedRecipient).forEach(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    (
      key:
        | "firstName"
        | "lastName"
        | "phoneNumber"
        | "passportOrIdSerialNumber"
        | "document"
        | "documentType"
        | "country"
        | "province"
        | "city"
        | "region"
        | "myAddresses",
    ) => {
      if (updatedRecipient[key] !== recipient[key]) {
        switch (key) {
          case "documentType":
            formData.append(
              key,
              updatedRecipient.documentType.recipientDocumentTypeId.toString(),
            );
            break;
          case "country":
            updatedRecipient?.country?.countryId &&
              formData.append(key, "" + updatedRecipient.country.countryId);
            break;
          case "province":
            updatedRecipient.province &&
              formData.append(key, "" + updatedRecipient.province.provinceId);
            break;
          case "city":
            updatedRecipient.city &&
              formData.append(key, "" + updatedRecipient.city.cityId);
            break;
          case "region":
            updatedRecipient.region &&
              formData.append(key, "" + updatedRecipient.region?.regionId);
            break;
          case "phoneNumber":
            updatedRecipient.phoneNumber &&
              formData.append(key, "" + updatedRecipient.phoneNumber);
            break;
          case "firstName":
            updatedRecipient.firstName &&
              formData.append(key, "" + updatedRecipient.firstName);
            break;
          case "lastName":
            updatedRecipient.lastName &&
              formData.append(key, "" + updatedRecipient.lastName);
            break;
          default:
            formData.append(key, "" + updatedRecipient[key]);
        }
      }
    },
  );

  if (!!Array.from(formData.values()).length) {
    const fileRequest =
      updatedRecipient["document"] !== recipient["document"]
        ? handleFileUpload(updatedRecipient.document)
        : Promise.resolve(null);

    try {
      return fileRequest.then((fileInfo) => {
        if (fileInfo) {
          formData.append("file", fileInfo);
        }

        const request = isNewRecipient
          ? recipientService.createRecipient(formData)
          : recipientService.editRecipient(recipient.recipientId, formData);

        return request
          .then((response: any) => {
            if (response.acknowledged || response.id) {
              return { status: SaveResultType.SUCCESS, payload: response };
            }
            return { status: SaveResultType.ERROR, payload: response };
          })
          .catch((e) => {
            if (e instanceof AxiosError) {
              return { status: SaveResultType.ERROR, payload: e };
            }
            return { status: SaveResultType.ERROR, payload: {} };
          });
      });
    } catch (e) {
      return Promise.resolve({ status: SaveResultType.ERROR, payload: e });
    }
  } else {
    return Promise.resolve({ status: SaveResultType.NO_CHANGE, payload: {} });
  }
}

function handleFileUpload(fileInfo: File | FileModel) {
  const isNewFile = fileInfo instanceof File;

  if (isNewFile) {
    return fileInfo
      ?.stream()
      .getReader()
      .read()
      .then((fileData) => {
        if (fileData.value) {
          const fileName = fileInfo?.name || "Document";

          return new File([fileData.value], fileName, { type: fileInfo?.type });
        } else {
          return null;
        }
      });
  }
  return Promise.resolve(null);
}

export function mapValidationErrors(e: AxiosError<any, any>) {
  const responseData = e.response && e.response.data || e;

  if (typeof responseData !== "string") {
    try {
      if (Array.isArray(responseData)) {
        return responseData.reduce(
          (
            result: { [key: string]: string },
            error: Joi.ValidationErrorItem,
          ) => {
            result[error.path[0] as string] = error.message;
            return result;
          },
          {},
        );
      } else {
        return {
          [responseData.path[0]]: responseData.message,
        };
      }
    } catch (e) {
      return {};
    }
  }

  return {};
}
